import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Permission, StudentDiploma, StudentDiplomaEditResource, StudentDiplomaViewResource } from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { StudentDiplomaPageBody } from "./StudentDiplomaPageBody";
import { StudentDiplomaPageHeader } from "./StudentDiplomaPageHeader";

const routeName = "studentDiploma";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  studentId?: number = 0;
}

export const studentDiplomaPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Diploma",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const StudentDiplomaPage = () => {
  const ROOT_PERMISSION: Permission = Permission.StudentDiploma;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const [diploma, setDiploma] = useState<StudentDiploma>({} as StudentDiploma);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [diploma] });
  const [viewResource, setViewResource] = useState<StudentDiplomaViewResource>({});
  const [editResource, setEditResource] = useState<StudentDiplomaEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);
    fetchViewResource();
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${studentDiplomaPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`);

  const fetch = (id: number) => {
    ApiService.studentDiplomas.get(id).then((response) => {
      if (id === 0) {
        if (optionalParams.studentId) response.studentId = optionalParams.studentId;
      }

      setDiploma(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = () => {
    ApiService.studentDiplomas.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.studentDiplomas.getEditResource().then((response) => {
      debugger;
      setEditResource(response);
    });
  };

  const pageHeader = (
    <StudentDiplomaPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={studentDiplomaPageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}
      fetch={fetch}
      diploma={diploma}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <StudentDiplomaPageBody
      crudMode={params.crudMode}
      diploma={diploma}
      setDiploma={setDiploma}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
    />
  );

  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      {pageBody}
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={pageHeader}
      content={pageBody}
    />
  );
};
